'use client';

import React, { useState } from 'react';
import { Plus, Search, Eye, X } from 'lucide-react';
import { MOCK_FEED_ISSUANCES, FeedIssuance, IssuanceStatus } from './feedData';

const STATUS_STYLES: Record<IssuanceStatus, string> = {
  Issued: 'bg-blue-100 text-blue-700',
  Pending: 'bg-amber-100 text-amber-700',
  Returned: 'bg-purple-100 text-purple-700',
  Partial: 'bg-orange-100 text-orange-700',
};

export default function FeedIssuanceTab() {
  const [issuances, setIssuances] = useState<FeedIssuance[]>(MOCK_FEED_ISSUANCES);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterFarm, setFilterFarm] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [viewItem, setViewItem] = useState<FeedIssuance | null>(null);
  const [form, setForm] = useState({ date: '2026-09-05', feedTypeName: '', feedCode: '', flockName: '', house: '', farm: 'Idudi Poultry Farm', quantityIssuedKg: '', birdCount: '', issuedBy: '', receivedBy: '', notes: '' });

  const farms = Array.from(new Set(issuances.map(i => i.farm)));

  const filtered = issuances.filter(i => {
    const q = search.toLowerCase();
    const matchSearch = !q || i.issuanceNumber.toLowerCase().includes(q) || i.flockName.toLowerCase().includes(q) || i.feedTypeName.toLowerCase().includes(q) || i.house.toLowerCase().includes(q);
    const matchStatus = filterStatus === 'all' || i.status === filterStatus;
    const matchFarm = filterFarm === 'all' || i.farm === filterFarm;
    return matchSearch && matchStatus && matchFarm;
  });

  function handleAdd() {
    if (!form.feedTypeName || !form.flockName || !form.quantityIssuedKg) return;
    const qty = Number(form.quantityIssuedKg);
    const newItem: FeedIssuance = {
      id: `fi-${Date.now()}`, issuanceNumber: `FI-2026-${String(issuances.length + 892).padStart(4, '0')}`,
      date: form.date, feedTypeId: '', feedTypeName: form.feedTypeName, feedCode: form.feedCode,
      flockId: '', flockName: form.flockName, house: form.house, farm: form.farm,
      quantityIssuedKg: qty, quantityReturnedKg: 0, quantityConsumedKg: qty, wastageKg: 0,
      issuedBy: form.issuedBy, receivedBy: form.receivedBy, status: 'Issued',
      notes: form.notes, birdCount: Number(form.birdCount), feedPerBirdGrams: Number(form.birdCount) > 0 ? Math.round((qty * 1000) / Number(form.birdCount)) : 0,
    };
    setIssuances(prev => [newItem, ...prev]);
    setShowModal(false);
  }

  const totalIssued = filtered.reduce((s, i) => s + i.quantityIssuedKg, 0);
  const totalConsumed = filtered.reduce((s, i) => s + i.quantityConsumedKg, 0);
  const totalWastage = filtered.reduce((s, i) => s + i.wastageKg, 0);

  return (
    <div>
      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { label: 'Total Issued (kg)', value: totalIssued.toLocaleString(), color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { label: 'Total Consumed (kg)', value: totalConsumed.toLocaleString(), color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Total Wastage (kg)', value: totalWastage.toFixed(1), color: 'text-amber-600', bg: 'bg-amber-50' },
        ].map(k => (
          <div key={k.label} className="p-4 rounded-xl border border-gray-100" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="text-xs text-gray-500 mb-1">{k.label}</div>
            <div className={`text-xl font-bold ${k.color}`}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search issuances…" className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300" style={{ backgroundColor: 'var(--card-bg)' }} />
        </div>
        <select value={filterFarm} onChange={e => setFilterFarm(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none" style={{ backgroundColor: 'var(--card-bg)' }}>
          <option value="all">All Farms</option>
          {farms.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none" style={{ backgroundColor: 'var(--card-bg)' }}>
          <option value="all">All Status</option>
          {(['Issued','Pending','Returned','Partial'] as IssuanceStatus[]).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg whitespace-nowrap" style={{ backgroundColor: 'var(--primary)' }}>
          <Plus size={14} /> Issue Feed
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100" style={{ backgroundColor: 'var(--surface)' }}>
              {['Issuance #','Date','Feed Type','Flock','House / Farm','Issued (kg)','Returned (kg)','Consumed (kg)','Wastage (kg)','Feed/Bird (g)','Issued By','Status',''].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(i => (
              <tr key={i.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors" style={{ backgroundColor: 'var(--card-bg)' }}>
                <td className="px-4 py-3 font-mono text-xs font-semibold text-indigo-600">{i.issuanceNumber}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{i.date}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="font-medium text-gray-800 text-xs">{i.feedTypeName}</div>
                  <div className="text-xs text-gray-400">{i.feedCode}</div>
                </td>
                <td className="px-4 py-3 font-medium text-gray-700 whitespace-nowrap">{i.flockName}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                  <div className="text-xs">{i.house}</div>
                  <div className="text-xs text-gray-400">{i.farm}</div>
                </td>
                <td className="px-4 py-3 font-semibold text-gray-800 text-right">{i.quantityIssuedKg.toLocaleString()}</td>
                <td className="px-4 py-3 text-right text-purple-600">{i.quantityReturnedKg > 0 ? i.quantityReturnedKg : '—'}</td>
                <td className="px-4 py-3 text-right text-emerald-600 font-medium">{i.quantityConsumedKg.toLocaleString()}</td>
                <td className="px-4 py-3 text-right text-amber-600">{i.wastageKg > 0 ? i.wastageKg.toFixed(1) : '—'}</td>
                <td className="px-4 py-3 text-right text-gray-600">{i.feedPerBirdGrams}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{i.issuedBy}</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_STYLES[i.status]}`}>{i.status}</span></td>
                <td className="px-4 py-3">
                  <button onClick={() => setViewItem(i)} className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-500 transition-colors"><Eye size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="py-12 text-center text-gray-400 text-sm">No issuance records found</div>}
      </div>

      {/* Issue Feed Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
          <div className="w-full max-w-lg rounded-2xl shadow-2xl p-6" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-gray-800">Issue Feed to Flock</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Date', key: 'date', type: 'date' },
                { label: 'Farm', key: 'farm', type: 'text' },
                { label: 'Feed Type Name', key: 'feedTypeName', type: 'text' },
                { label: 'Feed Code', key: 'feedCode', type: 'text' },
                { label: 'Flock Name', key: 'flockName', type: 'text' },
                { label: 'House', key: 'house', type: 'text' },
                { label: 'Quantity Issued (kg)', key: 'quantityIssuedKg', type: 'number' },
                { label: 'Bird Count', key: 'birdCount', type: 'number' },
                { label: 'Issued By', key: 'issuedBy', type: 'text' },
                { label: 'Received By', key: 'receivedBy', type: 'text' },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                  <input type={f.type} value={(form as any)[f.key]} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300" style={{ backgroundColor: 'var(--surface)' }} />
                </div>
              ))}
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none resize-none" style={{ backgroundColor: 'var(--surface)' }} />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleAdd} className="px-4 py-2 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: 'var(--primary)' }}>Issue Feed</button>
            </div>
          </div>
        </div>
      )}

      {/* View Detail Modal */}
      {viewItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
          <div className="w-full max-w-md rounded-2xl shadow-2xl p-6" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-800">{viewItem.issuanceNumber}</h3>
              <button onClick={() => setViewItem(null)} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
            </div>
            <div className="space-y-3 text-sm">
              {[
                ['Date', viewItem.date], ['Feed Type', viewItem.feedTypeName], ['Flock', viewItem.flockName],
                ['House', viewItem.house], ['Farm', viewItem.farm], ['Issued (kg)', viewItem.quantityIssuedKg],
                ['Returned (kg)', viewItem.quantityReturnedKg], ['Consumed (kg)', viewItem.quantityConsumedKg],
                ['Wastage (kg)', viewItem.wastageKg], ['Bird Count', viewItem.birdCount.toLocaleString()],
                ['Feed/Bird (g)', viewItem.feedPerBirdGrams], ['Issued By', viewItem.issuedBy],
                ['Received By', viewItem.receivedBy], ['Status', viewItem.status],
              ].map(([label, val]) => (
                <div key={String(label)} className="flex justify-between py-1.5 border-b border-gray-50">
                  <span className="text-gray-500">{label}</span>
                  <span className="font-medium text-gray-800">{String(val)}</span>
                </div>
              ))}
              {viewItem.notes && <div className="pt-2 text-gray-600 text-xs bg-gray-50 rounded-lg p-3">{viewItem.notes}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
